import { useEffect, useRef, useState } from "react";
import type { TouchEvent } from "react";
import { Link } from "react-router-dom";
import { topics } from "@/mocks/topics";
import { getArticleById } from "@/utils/content";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const total = topics.length;

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % total), 5200);
    return () => window.clearInterval(timer);
  }, [paused, total]);

  const go = (next: number) => setIndex(((next % total) + total) % total);

  const onTouchStart = (e: TouchEvent<HTMLElement>) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: TouchEvent<HTMLElement>) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? index + 1 : index - 1);
    touchStartX.current = null;
  };

  return (
    <section
      className="relative -mt-[60px] h-[500px] w-full overflow-hidden bg-secondary-900 pt-[60px] md:-mt-[96px] md:h-[640px] md:pt-[96px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {topics.map((topic, i) => (
        <div
          key={topic.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "pointer-events-none opacity-0"}`}
        >
          <img
            src={topic.cover}
            alt={topic.title}
            title={`${topic.title} - 噜噜社专题`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/85 via-secondary-950/40 to-secondary-950/30" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[1280px] flex-col justify-end px-4 pb-16 md:px-6 md:pb-20">
        <h1 className="text-xs font-semibold uppercase tracking-[0.3em] text-primary-300">
          噜噜社 · 综合娱乐资讯与潮流文化平台
        </h1>
        <span className="mt-4 inline-flex w-fit items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
          <i className="ri-fire-line" />
          焦点专题
        </span>
        <h2 className="mt-4 max-w-3xl text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
          {topics[index].title}
        </h2>
        <p className="mt-2 text-base font-medium text-primary-300 md:text-lg">{topics[index].subtitle}</p>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-100/90 line-clamp-3 md:text-base">
          {topics[index].intro}
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Link
            to={`/topic/${topics[index].id}`}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            查看专题
            <i className="ri-arrow-right-line" />
          </Link>
          <Link
            to={`/category/${getArticleById(topics[index].articleIds[0])?.category ?? "music"}`}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
          >
            浏览更多资讯
          </Link>
        </div>
      </div>

      {/* 控制按钮 */}
      <div className="absolute bottom-8 right-4 z-20 flex items-center gap-3 md:right-6">
        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一个专题"
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一个专题"
          className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>

      {/* 指示点 */}
      <div className="absolute bottom-8 left-4 z-20 flex items-center gap-2 md:left-6">
        {topics.map((t, i) => (
          <button
            key={t.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到${t.title}`}
            className={`h-1.5 cursor-pointer rounded-full transition-all duration-300 ${
              i === index ? "w-8 bg-primary-500" : "w-4 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}